// Board-sized copper pours are filled through a crysknife <mask> (a white reveal rect + black
// <path> cutouts). Chromium drops that mask under viewBox zoom on a dense board, so the pour
// vanishes. Baking the cutouts into the pour path as even-odd holes is pure vector: it survives
// any zoom in every browser, and matches the mask when the reveal covers the pour. Masks of any
// other shape are left untouched. Limitation: even-odd re-fills where cutouts overlap (rare for
// clearances) — an exact fix would need crysknife to union the cutouts.

const WHITE = new Set(['white', '#fff', '#ffffff', 'rgb(255,255,255)', 'rgb(255, 255, 255)']);
const BLACK = new Set(['', 'black', '#000', '#000000', 'rgb(0,0,0)', 'rgb(0, 0, 0)']);

// Cutout `d` strings if the mask is a plain reveal-rect + black-path cutouts; null otherwise.
function maskToHoles(mask: SVGMaskElement): string[] | null {
  const holes: string[] = [];
  let hasReveal = false;
  for (const child of Array.from(mask.children)) {
    const fill = (child.getAttribute('fill') ?? '').trim().toLowerCase();
    const tag = child.tagName.toLowerCase();
    if (WHITE.has(fill)) {
      if (tag !== 'rect') return null;
      hasReveal = true;
    } else if (BLACK.has(fill)) {
      const d = child.getAttribute('d');
      if (tag !== 'path' || !d) return null;
      holes.push(d);
    } else {
      return null;
    }
  }
  return hasReveal && holes.length ? holes : null;
}

// Returns the number of pours baked.
export function bakePourMasks(svg: SVGSVGElement): number {
  const bakedMaskIds = new Set<string>();
  let baked = 0;
  for (const pour of svg.querySelectorAll<SVGPathElement>('path[mask]')) {
    const id = pour.getAttribute('mask')!.match(/#([^)"']+)/)?.[1];
    const baseD = pour.getAttribute('d');
    if (!id || !baseD) continue;
    const mask = svg.querySelector<SVGMaskElement>(`mask[id="${id}"]`);
    if (!mask) continue;
    const holes = maskToHoles(mask);
    if (!holes) continue;
    pour.setAttribute('d', `${baseD} ${holes.join(' ')}`);
    pour.setAttribute('fill-rule', 'evenodd');
    pour.removeAttribute('mask');
    bakedMaskIds.add(id);
    baked += 1;
  }

  // Drop the mask defs that nothing references any more.
  if (bakedMaskIds.size) {
    const referenced = new Set<string>();
    for (const el of svg.querySelectorAll('[mask]')) {
      const ref = el.getAttribute('mask')!.match(/#([^)"']+)/)?.[1];
      if (ref) referenced.add(ref);
    }
    for (const id of bakedMaskIds) {
      if (!referenced.has(id)) svg.querySelector(`mask[id="${id}"]`)?.remove();
    }
  }

  return baked;
}
